import React from 'react';
import { t } from 'i18next';
import { Control, FieldValues, FieldPath } from 'react-hook-form';
import { useCountriesQuery } from '../../../services/utility';
import Select, { ControlledSelect, Option } from '../Select/Select';

interface CountrySelectProps<T extends FieldValues> {
  name?: FieldPath<T>;
  control?: Control<T>;
  editable: boolean;
  label?: string;
  isInfoDisplay?: boolean;
  className?: string;
  parent?: any;
  isControlled?: boolean;
  isMultiSelect?: boolean;
  handleInputChangeForAdress?: (event: React.ChangeEvent<HTMLInputElement>, field: string) => void;
  // Atomic country update: sets country_code and country_name in a single state
  // change. Preferred over handleInputChangeForAdress, which can only set one
  // field at a time and clobbers the other when called twice in a row.
  onCountryChange?: (country: { country_code: string; country_name: string }) => void;
}

const CountrySelect = <T extends FieldValues>({
  name,
  control,
  editable,
  label,
  isInfoDisplay,
  className = 'col-span-1',
  parent,
  isControlled = false,
  isMultiSelect = false,
  handleInputChangeForAdress,
  onCountryChange,
}: CountrySelectProps<T>) => {
  const { data, isLoading, error } = useCountriesQuery();

  const countryOptions: Option[] =
    data?.map(c => ({
      value: c.code,
      label: c.name,
    })) || [];

  const defaultCountry = countryOptions.find(c => {
    // country_code is the authoritative identifier; fall back to the name only
    // when no code is set (name can be locale-dependent).
    if (parent?.address?.country_code) {
      return c.value === parent?.address?.country_code;
    }
    if (parent?.address?.country_name) {
      return c.label === parent?.address?.country_name;
    }
    return false;
  });

  if (isLoading) return <div>Loading countries…</div>;
  if (error) return <div>Failed to load countries</div>;

  if (isControlled && name && control) {
    return (
      <ControlledSelect
        className={className}
        name={name as FieldPath<T>}
        control={control}
        options={countryOptions}
        containerClassName={className}
        label={label}
        placeholder={t('general.country')}
        disabled={!editable}
        isInfoDisplay={isInfoDisplay}
        isMultiSelect={isMultiSelect}
        isSearchable
      />
    );
  }

  return (
    <Select
      className={className}
      options={countryOptions}
      defaultValue={defaultCountry}
      label={label}
      placeholder={t('general.country')}
      disabled={!editable}
      isInfoDisplay={isInfoDisplay}
      isSearchable
      onChange={(selectedOption: Option) => {
        if (!selectedOption?.value) return;
        // Preferred path: update both fields atomically so they stay in sync.
        if (onCountryChange) {
          onCountryChange({ country_code: selectedOption.value, country_name: selectedOption.label });
          return;
        }
        // Fallback for callers that only wire a single-field setter. Update the
        // authoritative country_code; defaultCountry resolves the label from it.
        if (handleInputChangeForAdress) {
          handleInputChangeForAdress(
            { target: { value: selectedOption.value } } as React.ChangeEvent<HTMLInputElement>,
            'country_code',
          );
        }
      }}
    />
  );
};

export default CountrySelect;
